import Icon from '@/components/ui/AppIcon';

// ---------------------------------------------------------------------------
// ImagePlaceholder
// ---------------------------------------------------------------------------
// A branded stand-in for real photography. Fills its parent (which controls
// the aspect ratio), so the layout is final and a real <AppImage> can drop in
// later without reflow. Two modes:
//   • initials  → a person/brand avatar (used for team portraits)
//   • icon+label→ a labelled "photo goes here" tile (culture, process stages)
// ---------------------------------------------------------------------------

interface ImagePlaceholderProps {
  /** Render an initials avatar instead of the icon/label tile. */
  initials?: string;
  /** Small uppercase tag, e.g. "Photo placeholder". */
  label?: string;
  /** Optional descriptive line under the label. */
  caption?: string;
  /** AppIcon name for the icon/label variant. */
  icon?: string;
  className?: string;
}

export default function ImagePlaceholder({
  initials,
  label = 'Image placeholder',
  caption,
  icon = 'PaintBrushIcon',
  className = '',
}: ImagePlaceholderProps) {
  if (initials) {
    return (
      <div
        aria-hidden="true"
        className={`flex h-full w-full items-center justify-center bg-linear-to-br from-primary/20 via-muted to-secondary/15 ${className}`}
      >
        <span className="font-headline text-4xl font-bold text-primary/70">{initials}</span>
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={caption || label}
      className={`relative flex h-full w-full flex-col items-center justify-center gap-2.5 overflow-hidden bg-linear-to-br from-primary/12 via-bagasse to-leaf/12 px-6 text-center ${className}`}
    >
      {/* Oversized motif for depth  - purely decorative, clipped to a corner. */}
      <Icon
        name={icon}
        size={200}
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-10 -right-10 text-primary/5"
      />
      <span className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-surface/70 shadow-sm ring-1 ring-primary/15 backdrop-blur-sm">
        <Icon name={icon} size={30} className="text-primary/70" />
      </span>
      {label && (
        <span className="relative font-cta text-[11px] font-medium uppercase tracking-[0.18em] text-primary/60">
          {label}
        </span>
      )}
      {caption && (
        <span className="relative font-headline text-base font-semibold leading-snug text-foreground/75">
          {caption}
        </span>
      )}
    </div>
  );
}
